import { reactive, toRaw, watch } from 'vue'
import { defineStore } from 'pinia'
import { deepEqual } from 'fast-equals'
import { cloneDeep } from 'lodash-es'
import { tabMetrics } from '@/ContextTab/services/tabMetrics'
import { apiClientV1 } from '@/ContextTab/services/apiClient'
import type { GatewaySubscriptionHubMessage } from '@/Workers/Gateway/types'
import type { UserProfileSubscriptionMessageData } from '@/Workers/Gateway/gwService/types'
import type { DataOfUserProfileGetOut, UserProfilePatchIn } from '@/api/generated/v1'


const profileTemplate = Object.freeze({
  colors: null,
  default_dateline_agency: null,
  default_dateline_city: null,
  default_news_type_id: null,
  default_recipient_group_id: null,
  default_recipient_user_id: null,
  filters: null,
  fullscreen: null,
  newsEditor: null,
  panels: [],
  publishedPanelMode: null,
  soundNotifications: null,
} satisfies UserProfilePatchIn)

const STORAGE_USER_PROFILE = 'ps-user-profile'

export const useUserProfileStore = defineStore('userProfile', () => {

  const profile = reactive<DataOfUserProfileGetOut>({
    code: undefined,
    colors: {},
    default_dateline_agency: 'ИНТЕРФАКС',
    default_dateline_city: 'Москва',
    default_news_type_id: null,
    default_recipient_group_id: null,
    default_recipient_user_id: null,
    groups: [],
    panels: [],
    filters: {},
    name: '',
    publishedPanelMode: 1,
    keepOriginalNews: null,
    soundNotifications: null,
  })

  function isSameProfile(newProfile: UserProfilePatchIn) {
    const currentProfile = cloneDeep(profile)

    // check by patch fields only (not legacy fields, not ps-ui fields)
    // (same logic as for makePatch)
    return Object.keys(profileTemplate).reduce((acc, field) => {
      return newProfile[field] === undefined
        ? acc
        : acc && deepEqual(newProfile[field], currentProfile[field])
    }, true)
  }

  function makePatch(newProfile: UserProfilePatchIn) {
    const result: Record<string, any> = {}
    const currentProfile = cloneDeep(profile)

    // fields, listed in patch interface
    Object.keys(profileTemplate).forEach((field) => {
      result[field] = newProfile[field] === undefined
        ? currentProfile[field]
        : newProfile[field]
    })

    return result as UserProfilePatchIn
  }

  async function updateProfile(newProfile: UserProfilePatchIn) {
    if (isSameProfile(newProfile)) {
      return false
    }

    const currentProfile = cloneDeep(profile)
    const patch = makePatch(newProfile)

    Object.assign(profile, patch)

    const response = await apiClientV1.UsersService.patchProfile({ body: cloneDeep(patch) })

    if (response.error) {
      tabMetrics.sendException('Profile update error', response.error)
      Object.assign(profile, currentProfile)
      return
    }

    return response.data
  }

  async function fetchData() {
    const response = await apiClientV1.UsersService.getProfile()

    if (!response.error) {
      Object.assign(profile, response.data.data)
    }

    return response.data
  }

  function getIsUserInGroups(newsItemGroups: { group: { id: any } }[]) {
    return !!newsItemGroups.find((newsItemGroup) =>
      profile.groups?.find((profileGroup) =>
        newsItemGroup?.group?.id // to avoid compare undefined & undefined
        && newsItemGroup?.group?.id === profileGroup?.group?.id,
      ),
    )
  }

  function onGwUserProfileUpdate(message: GatewaySubscriptionHubMessage) {
    if (
      message.meta.fromCurrentSession
      || !message.payload.data
    ) {
      return
    }
    const data = message.payload.data as UserProfileSubscriptionMessageData

    const hasChanges = Object.entries(data.profile).reduce(
      (result, [key, value]) => result || !deepEqual(toRaw(profile[key]), value),
      false,
    )
    if (hasChanges) {
      Object.assign(profile, data.profile)
    }
  }

  // sync profile between tabs:

  watch(profile, () => {
    localStorage.setItem(
      STORAGE_USER_PROFILE,
      JSON.stringify({ profile, contextId: window.contextId }),
    )
  }, { deep: true })

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_USER_PROFILE) {
      const profileJson = localStorage.getItem(STORAGE_USER_PROFILE)
      if (profileJson) {
        try {
          const { profile: loadedProfile, contextId } = JSON.parse(profileJson)
          if (
            loadedProfile
            && typeof loadedProfile === 'object'
            && contextId !== window.contextId
            && !isSameProfile(loadedProfile)
          ) {
            Object.assign(profile, loadedProfile)
          }
        } catch (e) {
          console.error('Profile loaded from storage is incorrect JSON', e)
        }
      }
    }
  })

  return {
    profile,
    updateProfile,
    fetchData,
    getIsUserInGroups,
    onGwUserProfileUpdate,
  }
})
